import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { sourceBillingAPI } from "../services/api";
import BillingConditionsDetails from "./BillingConditionsDetails";

export default function SourceBillingConditions({ kind, sourceId, onConfirmed, onStatus }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);
  const statusCallback = useRef(onStatus);
  statusCallback.current = onStatus;

  useEffect(() => {
    let active = true;
    setResult(null);
    setError("");
    setChecked(false);
    statusCallback.current?.(false);
    sourceBillingAPI.preview(kind, sourceId).then(({ data }) => {
      if (!active) return;
      setResult(data);
      statusCallback.current?.(!data.confirmation_requise);
    }).catch((err) => {
      if (active) setError(err?.response?.data?.detail || "Conditions indisponibles");
    });
    return () => { active = false; };
  }, [kind, sourceId, reload]);

  const confirm = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await sourceBillingAPI.confirm(kind, sourceId, result.proposees);
      setResult(data);
      setChecked(false);
      statusCallback.current?.(!data.confirmation_requise);
      onConfirmed?.();
    } catch (err) {
      setChecked(false);
      setError(err?.response?.data?.detail || "Confirmation impossible");
    } finally {
      setSaving(false);
    }
  };

  return <section className="my-3 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
    <h3 className="text-sm font-semibold text-gray-900">Conditions des prochaines factures</h3>
    {error && <div role="alert" className="text-sm text-red-700">
      <p>{error}</p>
      <button type="button" className="underline" disabled={saving} onClick={() => setReload((value) => value + 1)}>Recharger les conditions</button>
    </div>}
    {!result && !error && <p className="text-sm text-gray-500">Chargement des conditions...</p>}
    {result && <>
      <BillingConditionsDetails terms={result.actuelles} mentions={result.mentions_actuelles} />
      {result.confirmation_requise && <p role="status" className="text-sm font-medium text-amber-800">Accord historique à confirmer avant de générer une nouvelle facture.</p>}
      {result.confirme_le && <p className="text-xs text-green-700">Conditions confirmées le {new Date(result.confirme_le).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris" })}.</p>}
      <details open={result.confirmation_requise}>
        <summary className="cursor-pointer text-sm text-primary-700">{result.confirmation_requise ? "Confirmer l'accord pour la suite" : "Mettre à jour les conditions pour la suite"}</summary>
        <div className="mt-3 space-y-3">
          {result.motif ? <div className="text-sm text-amber-800">
            <p>{result.motif}</p>
            <Link to="/clients" className="underline">Vérifier la fiche client</Link>
          </div> : <>
            <p className="text-xs text-gray-500">Conditions proposées à partir du régime du client. La devise, les tarifs, le délai et la TVA de cet accord sont conservés.</p>
            <BillingConditionsDetails terms={result.proposees} mentions={result.mentions_proposees} />
            <Link to="/clients" className="block text-xs text-primary-700 underline">Ajuster le régime dans la fiche client si nécessaire</Link>
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" className="mt-1" checked={checked} disabled={saving} onChange={(event) => setChecked(event.target.checked)} />
              <span>Je confirme que ces conditions correspondent à l'accord applicable aux prochaines factures, y compris le régime de pénalités présenté.</span>
            </label>
            <button type="button" className="btn-primary text-sm" disabled={!checked || saving} onClick={confirm}>{saving ? "Confirmation..." : "Confirmer pour les prochaines factures"}</button>
          </>}
          <p className="text-xs text-gray-500">Cette confirmation conserve les PDF et factures déjà émis. Toute nouvelle condition doit correspondre à l'accord convenu avec le client.</p>
        </div>
      </details>
    </>}
  </section>;
}
